import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, LocateFixed, MapPin, QrCode, RefreshCw } from 'lucide-react';
import type { EventCheckInResultDto, MyEventDto } from '@fas/types';
import { Alert, Button, StatusBadge } from '@fas/ui';
import { api, ApiError, errorMessage } from '@/lib/api';
import { deviceProof, prefetchDeviceChallenge } from '@/lib/device-proof';
import { getCurrentPosition, GPS_ERROR_MESSAGES, type GpsFailure, type GpsLocation } from '@/lib/geolocation';
import { clockTime, LOCATION_STATUS } from '@/features/attendance/labels';
import { usePublicSettings } from '@/features/attendance/hooks';
import { EVENT_ATTENDANCE, eventPlace, eventWhen } from './labels';

type Gps = { state: 'idle' } | { state: 'locating' } | { state: 'ok'; location: GpsLocation } | { state: 'error'; error: GpsFailure };

/**
 * Checking in to an institutional event (Phase 2E): with a scanned code (the event's own or a
 * display at the venue) or, where the event allows it, in the app. On site the location is
 * captured once (§14.4); doubts are flagged for the organizer, never refused.
 */
export function CheckInDialog({
  item,
  timeZone,
  scan,
  onDone,
  onClose,
}: {
  item: MyEventDto;
  timeZone: string;
  scan?: { receipt: string; label: string; scannedAt: string };
  onDone: (result: EventCheckInResultDto) => void;
  onClose: () => void;
}) {
  const { event, checkIn } = item;
  const [gps, setGps] = useState<Gps>({ state: 'idle' });
  const [result, setResult] = useState<EventCheckInResultDto | null>(null);
  const requestId = useRef(crypto.randomUUID());
  const settings = usePublicSettings();
  const disclosure = typeof settings.data?.['privacy.gps_disclosure_text'] === 'string' ? (settings.data['privacy.gps_disclosure_text'] as string) : null;
  const needsLocation = checkIn.locationRequired;
  const scanMissing = checkIn.requiresScan && !scan;

  const locate = useCallback(async () => {
    setGps({ state: 'locating' });
    const res = await getCurrentPosition();
    setGps(res.ok ? { state: 'ok', location: res.location } : { state: 'error', error: res.error });
  }, []);
  useEffect(() => {
    if (needsLocation && !scanMissing && gps.state === 'idle') void locate();
  }, [needsLocation, scanMissing, gps.state, locate]);
  useEffect(() => prefetchDeviceChallenge(), []);

  const submit = useMutation({
    networkMode: 'always',
    mutationFn: async () => {
      const clientRequestId = requestId.current;
      const device = await deviceProof('EVENT_CHECK_IN', clientRequestId).catch((err: unknown) => {
        if (err instanceof ApiError && err.code === 'NETWORK_ERROR') throw err;
        return null;
      });
      const where = needsLocation ? (gps.state === 'ok' ? { location: gps.location } : { gpsError: gps.state === 'error' ? gps.error : 'UNAVAILABLE' }) : {};
      return api.post<EventCheckInResultDto>(`/events/${event.id}/check-in`, {
        clientRequestId,
        ...where,
        ...(scan ? { scan: { receipt: scan.receipt } } : {}),
        ...(device ? { device } : {}),
      });
    },
    onSuccess: (res) => {
      setResult(res);
      onDone(res);
    },
  });

  if (result) {
    const a = result.attendance;
    return (
      <div className="space-y-4" aria-live="polite">
        <div className="flex items-start gap-3 rounded-lg border border-emerald-300 bg-emerald-50 p-4">
          <CheckCircle2 className="mt-0.5 size-6 shrink-0 text-emerald-700" aria-hidden />
          <div>
            <p className="text-lg font-semibold text-emerald-900">
              {result.alreadyCheckedIn ? 'Already checked in' : 'Checked in'} at {clockTime(a.checkedInAt, timeZone)}
            </p>
            <p className="text-sm text-emerald-900">{event.title}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <StatusBadge label={EVENT_ATTENDANCE[a.status].label} tone={EVENT_ATTENDANCE[a.status].tone} />
          {a.locationStatus ? <StatusBadge label={LOCATION_STATUS[a.locationStatus].label} tone={LOCATION_STATUS[a.locationStatus].tone} /> : null}
        </div>
        {a.flags.length > 0 ? (
          <Alert tone="warning" title="The organizer will review this check-in">
            <ul className="list-disc pl-4">
              {a.flags.map((f) => (
                <li key={f.type}>{f.message}</li>
              ))}
            </ul>
          </Alert>
        ) : null}
        <div className="flex justify-end">
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        submit.mutate();
      }}
    >
      <div>
        <p className="text-lg font-semibold">{event.title}</p>
        <p className="text-sm text-slate-600">
          {eventWhen(event, timeZone)} · {eventPlace(event)}
        </p>
      </div>
      {scan ? (
        <p className="flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-950">
          <QrCode className="size-4 shrink-0" aria-hidden />
          {scan.label} scanned at {clockTime(scan.scannedAt, timeZone)}.
        </p>
      ) : null}
      {scanMissing ? (
        <Alert tone="info" title="Scan the code at the venue">
          This event accepts check-ins by code only: scan the code shown at the venue with your phone’s camera.
        </Alert>
      ) : needsLocation ? (
        <section aria-labelledby="checkin-location" className="space-y-2 rounded-md border border-slate-200 p-3">
          <h3 id="checkin-location" className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
            <MapPin className="size-4" aria-hidden /> Location
          </h3>
          {disclosure ? <p className="text-xs text-slate-600">{disclosure}</p> : null}
          <div aria-live="polite" className="text-sm">
            {gps.state === 'locating' ? (
              <span className="inline-flex items-center gap-2 text-slate-700">
                <LocateFixed className="size-4 animate-pulse" aria-hidden /> Getting your location…
              </span>
            ) : null}
            {gps.state === 'ok' ? <span className="text-slate-800">Location captured. The server compares it with the venue.</span> : null}
            {gps.state === 'error' ? <Alert tone="warning">{GPS_ERROR_MESSAGES[gps.error]}</Alert> : null}
          </div>
          {gps.state === 'error' || gps.state === 'ok' ? (
            <Button type="button" variant="ghost" size="sm" onClick={() => void locate()}>
              <RefreshCw aria-hidden /> Try again
            </Button>
          ) : null}
        </section>
      ) : null}
      {submit.error ? (
        <Alert tone="danger" title="Not checked in">
          {submit.error instanceof ApiError && submit.error.code === 'NETWORK_ERROR'
            ? 'The server could not be reached. Check your connection and try again — retrying is safe.'
            : errorMessage(submit.error)}
        </Alert>
      ) : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" size="lg" loading={submit.isPending} disabled={scanMissing || gps.state === 'locating'}>
          Confirm check-in
        </Button>
      </div>
    </form>
  );
}
